import { Injectable } from "@angular/core";
import { ArrayHelper, Tools } from "@comunicare/comunicare-helper";
import { ALERT_STATUS, IFilter, IRule, IRuleAlert, IRuleDescription } from "@comunicare/comunicare-interfaces";
import { Observable } from "rxjs";
import { RuleAlert } from "src/app/models/rule-alert.model";
import { ApiService } from "./api.service";
import { BaseApiService } from "./baseApi.service";

// Above this number of services, we don't send them in the URL at all: the backend then falls back to
// all the services available to the requester, which avoids building an excessively long query string.
const MAX_SERVICES_IN_URL = 10;

/**
 * Parameters accepted by GET dashboard/ruleAlertsInfos and GET dashboard/ruleAlertsInfos/count
 */
export interface IRuleAlertsInfosParams {
  pageNumber?: number;
  pageSize?: number;
  services?: string[];
  alertLevels?: number[];
  period?: string;
  date?: string;
  monitoringServicesIds?: string[];
  careplanIds?: string[];
  alertStatus?: ALERT_STATUS;
  onlyNoDataTransmissionAlert?: boolean;
  sortId?: string;
  sortOrder?: "asc" | "desc" | "";
  filters: IFilter[];
}

@Injectable({
  providedIn: "root",
})
export class RuleAlertApiService extends BaseApiService {
  constructor(private api: ApiService) {
    super(
      "RuleAlerts", // Theme !
      ["dashboard/rule"], // Post route paths
      [
        "rulealerts", // 0
        "dashboard/lastNoDataTransmissionRuleAlert", // 1
        "dashboard/rulesDescription", // 2
        "dashboard/ruleAlertsInfos", // 3
        "dashboard/ruleAlertsInfos/count", // 4
      ], // Get route paths
      ["rulesAlert"], // Put route paths
      [] // Delete route paths
    );
  }

  public list(
    alertLevel: number,
    services: string[],
    monitoringServicesIds: string[],
    onlyNoDataTransmissionAlert: boolean,
    alertStatus: ALERT_STATUS
  ): Observable<IRuleAlert[]> {
    const params: {
      alert: number;
      onlyNoDataTransmissionAlert: boolean;
      services?: string;
      monitoringServicesIds?: string;
      alertStatus: ALERT_STATUS;
    } = { alert: alertLevel, onlyNoDataTransmissionAlert, alertStatus };
    if (Tools.isDefined(services) && !ArrayHelper.isEmpty(services)) {
      params.services = JSON.stringify(services);
    }
    if (Tools.isDefined(monitoringServicesIds) && !ArrayHelper.isEmpty(monitoringServicesIds)) {
      params.monitoringServicesIds = monitoringServicesIds.join();
    }
    return this.api.get(this.readRoutes[0], params) as Observable<IRuleAlert[]>;
  }

  public listInfos(params: IRuleAlertsInfosParams): Observable<IRuleAlert[]> {
    return this.api.get(this.readRoutes[3], {
      pageNumber: params.pageNumber,
      pageSize: params.pageSize,
      services:
        Tools.isDefined(params.services) && !ArrayHelper.isEmpty(params.services) && params.services.length <= MAX_SERVICES_IN_URL
          ? JSON.stringify(params.services)
          : undefined,
      alert: params.alertLevels?.join(),
      period: params.period,
      date: params.date,
      monitoringServicesIds: params.monitoringServicesIds?.join(),
      careplans: params.careplanIds?.join(),
      alertStatus: params.alertStatus,
      onlyNoDataTransmissionAlert: params.onlyNoDataTransmissionAlert,
      sortId: params.sortId,
      sortOrder: params.sortOrder,
      filters: JSON.stringify(params.filters),
    }) as Observable<IRuleAlert[]>;
  }

  public countInfos(params: IRuleAlertsInfosParams): Observable<{ count: number }> {
    return this.api.get(this.readRoutes[4], {
      services:
        Tools.isDefined(params.services) && !ArrayHelper.isEmpty(params.services) && params.services.length <= MAX_SERVICES_IN_URL
          ? JSON.stringify(params.services)
          : undefined,
      alert: params.alertLevels?.join(),
      period: params.period,
      date: params.date,
      monitoringServicesIds: params.monitoringServicesIds?.join(),
      careplans: params.careplanIds?.join(),
      alertStatus: params.alertStatus,
      onlyNoDataTransmissionAlert: params.onlyNoDataTransmissionAlert,
      filters: JSON.stringify(params.filters),
    }) as Observable<{ count: number }>;
  }

  public listForPatient(patientId: string, alertsLevels?: string[]): Observable<IRuleAlert[]> {
    return this.api.get(this.readRoutes[0], {
      identifier: patientId,
      alert: alertsLevels ? alertsLevels.join(",") : undefined,
    }) as Observable<IRuleAlert[]>;
  }

  public save(ruleAlert: RuleAlert): Observable<IRuleAlert[]> {
    const body: IRuleAlert = {
      _id: ruleAlert._id,
      identifier: ruleAlert.identifier,
      subject: ruleAlert.subject,
      ruleMeta: ruleAlert.ruleMeta,
      rule: ruleAlert.rule,
      valueComponents: ruleAlert.valueComponents,
      observation: ruleAlert.observation,
      status: ruleAlert.status,
      comment: ruleAlert.comment,
      questionnaireResponse: ruleAlert.questionnaireResponse,
      author: ruleAlert.author,
      notes: ruleAlert.notes,
      created_by: ruleAlert.created_by,
      actionsHistory: ruleAlert.actionsHistory,
      alertStatus: ruleAlert.alertStatus,
      noDataTransmission: ruleAlert.noDataTransmission,
    };
    return this.api.put(this.updateRoutes[0], body) as Observable<IRuleAlert[]>;
  }

  public listNoDataTransmission(caremateId: string): Observable<RuleAlert> {
    return this.api.get(this.readRoutes[1], {
      caremateId,
    }) as Observable<RuleAlert>;
  }

  /**
   * Creates a new rule
   * @param rule The new rule to save as an IRule
   * @returns The saved rule
   */
  public createRule(rule: IRule): Observable<IRule> {
    return this.api.post(this.createRoutes[0], rule) as Observable<IRule>;
  }

  /**
   * Get rules description
   * @param identifier The patient caremateId
   * @param lang
   * @returns The list of rules descriptions
   */
  public getRulesDescriptions(identifier: string, lang: string): Observable<IRuleDescription[]> {
    return this.api.get(this.readRoutes[2], {
      identifier,
      lang,
    }) as Observable<IRuleDescription[]>;
  }
}
